/*
 *  -----------------------------------------------------------------------
 *    <copyright file="visit-tab-grid.tsx" company="Triumph Research Intelligence">
 *        Copyright (C) 2019 by Triumph Research Intelligence. All rights reserved.
 *    </copyright>
 *   -----------------------------------------------------------------------
 *
 */

import React, { useEffect, useState } from 'react';

import { UseBoolean } from 'react-hanger';
import { orderBy } from '@progress/kendo-data-query';
import AssignVisitDialog from '@components/admin/assign-visit';
import UnassignVisitConfirmanionDialog from '@components/admin/unassign-visit-confirmation-dialog';
import {
    ASSOCIATED_VISIT_TAB_GRID_COLUMNS,
    VISIT_TAB_GRID_COLUMNS,
} from '@components/admin/visits-tab/visit-tab-grid/visit-tab-grid.data';
import { VisitsTabGridProps } from '@components/admin/visits-tab/visit-tab-grid/visit-tab-grid.interface';
import ExpandableGridV2 from '@components/core/expandable/grid-v2';
import {
    ExcelExportConfig,
    IActionItemV2,
} from '@components/core/expandable/grid-v2/interfaces-v2/grid-props-v2.interface';
import { ExtraMenuItem, ExtraMenuItemPosition } from '@components/core/grid-header/grid-menu/grid-menu.interface';
import Loading from '@components/core/loading';
import { restructuredUpdatedCreatedBy } from '@utils-clean/update-users-array';
import { useBoolean, useTranslation } from '@hooks/index';
import NotificationService from '@utils/notification';
import { StudyVisitScheduleFields, StudySubjectVisitFields } from '@queries/generated/fragments.generated';

const VisitsTabGrid = ({
    currentScheduleId,
    currentScheduleVersion,
    visits,
    studyId,
    study,
    currentVisit,
    setCurrentVisit,
    canEdit,
    showNewVisit,
    switchMenu,
    showBulkEditDialog,
    showEditSequenceWarning,
    assignedItem,
    disableTools,
}: VisitsTabGridProps) => {
    const { t } = useTranslation();

    const [newVisits, setNewVisits] = useState([] as any);

    const openAssignVisit = useBoolean(false);
    const openUnassignVisit = useBoolean(false);

    const associatedView = !!assignedItem;

    const findCurrentVisit = (inputVisits: StudySubjectVisitFields[], currentVisitItem: StudySubjectVisitFields) =>
        inputVisits.find((visitItem: StudySubjectVisitFields) => visitItem.id === currentVisitItem.id);

    useEffect(() => {
        let visitsArray = [...(visits || [])];

        if (assignedItem) {
            visitsArray = visitsArray.filter((visit) =>
                visit?.eventIds ? visit.eventIds.findIndex((id) => id === assignedItem.id) >= 0 : false,
            );
        }

        const newData = restructuredUpdatedCreatedBy(visitsArray);
        visitsArray = orderBy(newData, [{ field: 'sequenceNumber', dir: 'asc' }]) ?? [];
        visitsArray = visitsArray.map((item: any) => {
            const readableLeadVisitId = visits?.find((visitFormGrid: any) => visitFormGrid.id === item.leadVisitId);
            return {
                ...item,
                _endDateUsage: item.isEndDateEarliest ? 'Earliest' : 'Latest',
                _crfsCount: item.eventIds.length,
                _readableLeadVisitId: readableLeadVisitId?.visitId ?? '',
            };
        });
        setNewVisits(visitsArray);

        if (!assignedItem) {
            if (!currentVisit) {
                setCurrentVisit(visitsArray[0]);
            } else {
                const visitForUpdate = findCurrentVisit(visitsArray, currentVisit);
                setCurrentVisit({ ...visitForUpdate });
            }
        }
    }, [visits, assignedItem]);

    const extraMenuItems: ExtraMenuItem[] = associatedView
        ? [
              {
                  position: ExtraMenuItemPosition.expandMenu,
                  customClassName: 'grid-header__item--always-visible',
                  expandMenuItems: [
                      {
                          label: 'admin:assignVisitS',
                          onClick: () => openAssignVisit.setTrue(),
                          icon: ['fal', 'add'],
                          enabled: !disableTools && canEdit,
                      },
                  ],
              },
          ]
        : [
              {
                  position: ExtraMenuItemPosition.expandMenu,
                  customClassName: 'grid-header__item--always-visible',
                  expandMenuItems: [
                      {
                          label: 'admin:addVisit',
                          onClick: () => showNewVisit.setTrue(),
                          icon: ['fal', 'add'],
                          enabled: !disableTools && canEdit,
                      },
                      {
                          label: 'admin:editVisits',
                          onClick: (selectedVisits: StudyVisitScheduleFields[]) => {
                              if (showBulkEditDialog) {
                                  showBulkEditDialog(selectedVisits);
                              }
                          },
                          icon: ['fal', 'pen-to-square'],
                          enabled: (selectedRisks: any) => !!selectedRisks.length && !disableTools && canEdit,
                          requireSelection: true,
                      },
                      {
                          label: 'admin:editVisitSequence',
                          onClick: () => showEditSequenceWarning?.setTrue(),
                          icon: ['fal', 'arrows-up-down-left-right'],
                          enabled: !disableTools && canEdit,
                      },
                  ],
              },
          ];

    const getRowActions = (visit: StudyVisitScheduleFields): IActionItemV2[] =>
        associatedView
            ? [
                  {
                      label: 'admin:unassignVisit',
                      icon: ['fal', 'unlink'],
                      onClick: () => {
                          setCurrentVisit(visit);
                          openUnassignVisit.setTrue();
                      },
                      enabled: canEdit,
                  },
              ]
            : [];

    const closeVisitDialog = (dialogToClose: UseBoolean) => (action?: string) => {
        if (action === 'error') {
            NotificationService.showNotification(t('errorMessage'));
        } else {
            dialogToClose.setFalse();
        }
    };

    const exportFileName = `${study?.studyNumber}_SVT_Visits ${
        currentScheduleVersion ? `${currentScheduleVersion}` : ''
    }`;
    const exportConfig: ExcelExportConfig = {
        fileName: exportFileName,
        useColumn: true,
        useData: true,
    };

    if (!studyId || !visits) return <Loading />;

    return (
        <>
            {openAssignVisit.value && !!assignedItem && (
                <AssignVisitDialog
                    visits={visits}
                    studyId={studyId}
                    currentScheduleId={currentScheduleId}
                    assignedItem={assignedItem}
                    close={closeVisitDialog(openAssignVisit)}
                />
            )}
            {openUnassignVisit.value && !!currentVisit && !!assignedItem && (
                <UnassignVisitConfirmanionDialog
                    studyId={studyId}
                    currentScheduleId={currentScheduleId}
                    currentVisit={currentVisit}
                    assignedItem={assignedItem}
                    close={closeVisitDialog(openUnassignVisit)}
                />
            )}
            <ExpandableGridV2
                className="svt-visit-grid-vertical-scroll"
                title={t(associatedView ? 'common:assignedVisits' : 'common:visits')}
                customKey="visits-grid"
                id="visitsGrid"
                key="visitsGrid"
                items={newVisits}
                hideCheckbox
                hideExpand
                scrollable="scrollable"
                groupable
                reorderable
                showFirstColumn={associatedView}
                resizable
                pageable={false}
                fixedScroll={!associatedView}
                selectable={{ enabled: !associatedView && !disableTools }}
                defaultSelected={!associatedView ? (currentVisit ?? newVisits[0]) : undefined}
                setCurrentItem={setCurrentVisit}
                switchMenu={!associatedView ? switchMenu : undefined}
                rowActions={getRowActions.length > 1 ? getRowActions : undefined}
                extraMenuItems={extraMenuItems}
                columns={associatedView ? ASSOCIATED_VISIT_TAB_GRID_COLUMNS(t) : VISIT_TAB_GRID_COLUMNS(t)}
                disableTools={disableTools}
                exportConfig={!associatedView ? exportConfig : null}
            />
        </>
    );
};

export default VisitsTabGrid;
